<template>
  <div v-if="rulesList && rulesList.length !== 0">
    <div
      v-if="hideCountry()"
      class="offer-card__down-item"
    >
      <h5 class="offer-card__title-inner">
        {{ $t('affiliates.offersView.labels.countryFilter') }}
      </h5>
      <span class="text-tooltip">
        <span class="label">{{ $t('affiliates.offer_popup.only_selected') }}:</span>
        {{ getCountryCode.map(getCountryName).join(', ') }}
      </span>
    </div>
    <div
      v-if="hasRevShare"
      class="t:flex rules-container"
    >
      <div
        v-if="rulesList[isActiveRule].settings.rev_share.sport_levels !== null"
        class="rule"
      >
        <div class="title-blue">
          {{ rulesList[isActiveRule].settings.rev_share.casino_levels !== null ?
            $t('affiliates.affiliatesOffers.rev_sport') :
            $t('affiliates.affiliatesOffers.revshare')
          }}
        </div>
        <div class="offer-card__table">
          <div
            v-for="(level, i) in rulesList[isActiveRule].settings.rev_share.sport_levels"
            :key="i"
            class="offer-card__table-row t:justify-between"
          >
            <div class="t:flex">
              <UiIcon
                name="greater-than-equal"
                class="less-or-equal"
              />
              <span class="t:ml-4">{{ level.ftd_count }} FTD</span>
            </div>
            <span>{{ level.percent }}%</span>
          </div>
        </div>
      </div>

      <div
        v-if="rulesList[isActiveRule].settings.rev_share.casino_levels !== null"
        class="rule"
      >
        <div class="title-blue">
          {{ $t('affiliates.affiliatesOffers.rev_casino') }}
        </div>

        <div class="offer-card__table">
          <div
            v-for="(level, i) in rulesList[isActiveRule].settings.rev_share.casino_levels"
            :key="i"
            class="offer-card__table-row t:justify-between"
          >
            <div class="t:flex">
              <UiIcon
                name="greater-than-equal"
                class="less-or-equal"
              />
              <span class="t:ml-4">{{ level.ftd_count }} FTD</span>
            </div>
            <span>{{ level.percent }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- CPA -->
    <div
      v-for="(cpaItem, i) in rulesList[isActiveRule].settings.cpa"
      :key="`dep-${i}`"
      class="t:mt-4"
    >
      <div
        v-if="cpaItem.amount !== null"
        class="title-blue"
      >
        {{ $t('affiliates.affiliatesOffers.cpa') }}
      </div>
      <div class="line root">
        <div
          v-if="cpaItem.amount !== null"
          class="offer-card__table"
        >
          <div class="offer-card__table-row">
            <span>{{ $t('affiliates.affiliatesOffers.payout') }}</span>
            <span> <Vnodes :vnodes="renderOfferInfo('amount', 'uncountable', i)" /></span>
          </div>
          <div
            v-if="offerInfo.cap_players_count !== null && offerInfo.cap_period_seconds !== null"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.cap_players') }}</span>
            <span>
              <Vnodes :vnodes="renderOfferInfo('daily_cap', 'uncountable', i)" />
            </span>
          </div>
          <div
            v-if="offerInfo.global_hold_seconds_cpa !== null"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.hold') }}</span>
            <span>
              <Vnodes :vnodes="renderOfferInfo('hold_seconds', 'uncountable', i)" />
            </span>
          </div>
        </div>
      </div>

      <!-- deposits -->
      <div
        v-if="hasDeposits(cpaItem)"
        class="line"
      >
        <div class="title-blue">
          {{ $t('affiliates.affiliatesOffers.deposit') }}
        </div>
        <div
          v-if="viewRules('deposits_count', i) || viewRules('deposits_sum', i) || viewRules('deposit_amount', i)"
          ref="deposits"
          class="offer-card__table"
        >
          <div
            v-if="viewRules('deposits_count', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.deposit_count') }}</span>
            <Vnodes :vnodes="renderMinMax('deposits_count', i)" />
          </div>

          <div
            v-if="viewRules('deposits_sum', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.deposit_total') }}</span>
            <Vnodes :vnodes="renderMinMax('deposits_sum', i)" />
          </div>

          <div
            v-if="viewRules('deposit_amount', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.one_deposit') }}</span>
            <Vnodes :vnodes="renderMinMax('deposit_amount', i)" />
          </div>
        </div>
      </div>
      <!-- Sport Bets -->
      <div
        v-if="hasSportBets(cpaItem)"
        class="line"
      >
        <div class="title-blue">
          {{ $t('affiliates.affiliatesOffers.sport_bet') }}
        </div>
        <div
          v-if="viewRules('sport_bets_count', i) ||
            viewRules('sport_bets_sum', i) ||
            viewRules('sport_bet_amount', i) ||
            viewRules('sport_bets_odd', i)"
          ref="sport_bet"
          class="offer-card__table"
        >
          <div
            v-if="viewRules('sport_bets_count', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.bet_count') }}</span>
            <Vnodes :vnodes="renderMinMax('sport_bets_count', i)" />
          </div>

          <div
            v-if="viewRules('sport_bets_sum', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.bet_sum') }}</span>
            <Vnodes :vnodes="renderMinMax('sport_bets_sum', i)" />
          </div>

          <div
            v-if="viewRules('sport_bet_amount', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.one_bet_amount') }}</span>
            <Vnodes :vnodes="renderMinMax('sport_bet_amount', i)" />
          </div>

          <div
            v-if="viewRules('sport_bets_odd', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.bet_odd') }}</span>
            <Vnodes :vnodes="renderMinMax('sport_bets_odd', i)" />
          </div>
        </div>
      </div>
      <!-- Casino Bets -->
      <div
        v-if="hasCasinoBets(cpaItem)"
        class="line"
      >
        <div class="title-blue">
          {{ $t('affiliates.affiliatesOffers.casino_bet') }}
        </div>
        <div
          v-if="viewRules('casino_bets_count', i) || viewRules('casino_bets_sum', i) || viewRules('casino_bet_amount', i)"
          ref="casino_bet"
          class="offer-card__table"
        >
          <div
            v-if="viewRules('casino_bets_count', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.bet_count') }}</span>
            <Vnodes :vnodes="renderMinMax('casino_bets_count', i)" />
          </div>

          <div
            v-if="viewRules('casino_bets_sum', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.bet_sum') }}</span>
            <Vnodes :vnodes="renderMinMax('casino_bets_sum', i)" />
          </div>

          <div
            v-if="viewRules('casino_bet_amount', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.affiliatesOffers.one_bet_amount') }}</span>
            <Vnodes :vnodes="renderMinMax('casino_bet_amount', i)" />
          </div>
        </div>
      </div>
      <div
        v-if="isActivityAvailable && cpaItem.player_activity"
        class="line"
      >
        <div class="title-blue">
          {{ $t('affiliates.offer_popup.player_activity') }}
        </div>
        <div
          ref="casino_bet"
          class="offer-card__table"
        >
          <div class="offer-card__table-row">
            <span>{{ $t('affiliates.offer_popup.min_interval') }}</span>
            <span>{{ $t(`affiliates.time.${cpaItem.player_activity.interval_unit}`, { count: cpaItem.player_activity.interval_value }, cpaItem.player_activity.interval_value) }}</span>
          </div>
          <div class="offer-card__table-row">
            <span class="ui-6">{{ $t('affiliates.offer_popup.required_activities') }}</span>
            <span v-if="cpaItem.player_activity.event_types_group !== 'selected'">
              {{ $t(`affiliates.offer_popup.type.${cpaItem.player_activity.event_types_group}`) }}
            </span>
            <span
              v-else
              class="offer-card__table-row_right"
            >
              {{ cpaItem.player_activity.selected_event_types.map(el => $t(`affiliates.offer_popup.event.${el}`)).join(', ') }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="hasPlayerRegistration(cpaItem)"
        class="line"
      >
        <div class="title-blue">
          {{ $t('affiliates.offer_popup.player_registration') }}
        </div>
        <div
          v-if="viewRules('player_registration', i)"
          ref="player_registration"
          class="offer-card__table"
        >
          <div
            v-if="viewRules('player_registration', i)"
            class="offer-card__table-row"
          >
            <span>{{ $t('affiliates.offer_popup.player_reg') }}</span>
            <Vnodes :vnodes="renderMinMax('player_registration', i)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import { mapGetters } from 'vuex';
import { formatDuration, intervalToDuration } from 'date-fns';
import { config } from '@/config';
import Vnodes from '@/components/VNodes.vue';
import { durationSecondsToDays } from '@/helpers/date-helper';
import { locales } from '@/helpers/date-locales';

const isActivityAvailable = ['demo', 'test', 'betwinner', 'betpari'].includes(config.customerId);

const depositKeys = [
  'deposits_count',
  'deposits_sum',
  'deposit_amount',
].map((key) => [`${key}_max`, `${key}_min`]).flat();

const sportBetsKeys = [
  'sport_bets_count',
  'sport_bets_sum',
  'sport_bet_amount',
  'sport_bets_odd',
].map((key) => [`${key}_max`, `${key}_min`]).flat();

const casinoBetsKeys = [
  'casino_bets_count',
  'casino_bets_sum',
  'casino_bet_amount',
].map((key) => [`${key}_max`, `${key}_min`]).flat();

const playerRegistrationKeys = [
  'player_registration',
].map((key) => [`${key}_max`, `${key}_min`]).flat();

export default {
  name: 'OfferRulesContent',

  components: {
    Vnodes,
  },

  props: {
    rulesList: {
      type: Array,
      required: true,
    },
    isActiveRule: {
      type: Number,
      required: true,
    },
    offerInfo: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isActivityAvailable,
    };
  },

  computed: {
    ...mapGetters({
      countries: 'misc/countries',
      currencies: 'misc/currencies',
    }),

    hasRevShare() {
      // const amount = this.$_.get(this.rulesList[this.isActiveRule], ['settings', 'cpa', 0, 'amount']);
      //
      // if (this.rulesList[this.isActiveRule].settings.rev_share) {
      //   return [
      //     this.rulesList[this.isActiveRule].settings.rev_share?.sport_levels,
      //     this.rulesList[this.isActiveRule].settings.rev_share?.casino_levels,
      //   ].some(this.hasRs) || !amount;
      // }

      return !!this.rulesList[this.isActiveRule].settings.rev_share;
    },

    getCountryCode() {
      let cc = [''];
      if (this.offerInfo.payout_rules[this.isActiveRule].filters.hasOwnProperty('country_code')) {
        cc = this.offerInfo.payout_rules[this.isActiveRule].filters.country_code;
      }
      return cc;
    },
  },

  methods: {
    getCurrencyLabel(code) {
      const currency = this.currencies.find(((el) => el.code === code));

      return currency?.symbol || `${code} `;
    },

    hasRs(lvl) {
      return lvl?.some((el) => !!+el.percent);
    },

    hasDeposits(cpa) {
      return Object.values(this.$_.pick(cpa, depositKeys)).some((el) => !!el || el === 0);
    },

    hasSportBets(cpa) {
      return Object.values(this.$_.pick(cpa, sportBetsKeys)).some((el) => !!el || el === 0);
    },

    hasCasinoBets(cpa) {
      return Object.values(this.$_.pick(cpa, casinoBetsKeys)).some((el) => !!el || el === 0);
    },

    hasPlayerRegistration(cpa) {
      return Object.values(this.$_.pick(cpa, playerRegistrationKeys)).some((el) => !!el || el === 0);
    },

    humanizeDuration(value) {
      if (value === 0) {
        return 0;
      }
      const duration = intervalToDuration({
        start: new Date(0),
        end: new Date(value * 1000),
      });
      return formatDuration(duration, { locale: locales[this.$i18n.locale] });
    },

    viewRules(property, i) {
      const offerPropertyMin = `${property}_min`;
      const offerPropertyMax = `${property}_max`;
      const min = this.$_.get(this.rulesList, [this.isActiveRule, 'settings', 'cpa', i, offerPropertyMin], null);
      const max = this.$_.get(this.rulesList, [this.isActiveRule, 'settings', 'cpa', i, offerPropertyMax], null);
      return !(min === null && max === null);
    },

    getCountryName(code) {
      const c = this.countries.filter((country) => country.code === code);
      if (c.length) {
        return c[0].name;
      }
      return '';
    },

    hideCountry() {
      if (this.rulesList[this.isActiveRule].filters.hasOwnProperty('country_code')) {
        return this.rulesList[this.isActiveRule].filters.country_code.length !== 0;
      }
      return false;
    },

    renderOfferInfoPerDays(index) {
      const cap_players_count_val = this.rulesList[this.isActiveRule].settings.cpa[index].cap_players_count;

      if (cap_players_count_val === 0) {
        return h('span', {}, '0');
      }

      const cap_period_days_val = durationSecondsToDays(
        this.rulesList[this.isActiveRule].settings.cpa[index].cap_period_seconds,
      );
      return h(
        'span',
        {},
        this.$t(
          'affiliates.affiliatesOffers.cap_players_per_days',
          {
            count: cap_players_count_val,
            days: cap_period_days_val,
          },
          cap_period_days_val,
        ),
      );
    },

    renderOfferInfo(offerProperty, translate, index) {
      let span = h(
        'span',
        {},
        this.$t(`affiliates.affiliatesOffers.${translate}`, {
          count: this.rulesList[this.isActiveRule].settings.cpa[index][offerProperty],
        }),
      );

      if (offerProperty === 'daily_cap') {
        return this.renderOfferInfoPerDays(index);
      }

      if (offerProperty === 'hold_seconds') {
        const hold_seconds_cpa =
          this.humanizeDuration(this.rulesList[this.isActiveRule].settings.cpa[index].hold_seconds);
        span = h('span', {}, this.$t(`affiliates.affiliatesOffers.${translate}`, { count: hold_seconds_cpa }));
      }

      if (offerProperty === 'amount') {
        const currencyLabel =
          this.getCurrencyLabel(this.rulesList[this.isActiveRule].settings.cpa[index].currency_code);
        const cpa_payout =
          currencyLabel + this.$filters.formatMoney(this.rulesList[this.isActiveRule].settings.cpa[index].amount);

        span = h('span', {}, this.$t(`affiliates.affiliatesOffers.${translate}`, { count: cpa_payout }));
      }

      return span;
    },

    renderMinMax(offerProperty, index) {
      const offerPropertyMin = `${offerProperty}_min`;
      const offerPropertyMax = `${offerProperty}_max`;

      let offerPropertyMinValue = this.rulesList[this.isActiveRule].settings.cpa[index][offerPropertyMin];
      let offerPropertyMaxValue = this.rulesList[this.isActiveRule].settings.cpa[index][offerPropertyMax];

      const hasMinValue = !!offerPropertyMinValue || offerPropertyMinValue === 0;
      const hasMaxValue = !!offerPropertyMaxValue || offerPropertyMaxValue === 0;

      if (!['deposits_count', 'sport_bets_count', 'sport_bets_odd', 'casino_bets_count', 'player_registration'].includes(offerProperty)) {
        const currencyLabel = this.getCurrencyLabel(this.rulesList[this.isActiveRule].settings.cpa[index].currency_code);

        if (hasMinValue) {
          offerPropertyMinValue = currencyLabel
            + this.$filters.formatMoney(this.rulesList[this.isActiveRule].settings.cpa[index][offerPropertyMin]);
        }

        if (hasMaxValue) {
          offerPropertyMaxValue = currencyLabel
            + this.$filters.formatMoney(this.rulesList[this.isActiveRule].settings.cpa[index][offerPropertyMax]);
        }
      }

      const spanMin = h('span', {}, this.$t('affiliates.affiliatesOffers.min', { count: offerPropertyMinValue }));
      const spanSeparator = h('span', {}, ' - ');
      const spanMax = h('span', {}, this.$t('affiliates.affiliatesOffers.max', { count: offerPropertyMaxValue }));
      const elementsArray = [];

      if (hasMinValue && hasMaxValue) {
        elementsArray.push(spanMin);
        elementsArray.push(spanSeparator);
        elementsArray.push(spanMax);
      } else if (hasMinValue) {
        elementsArray.push(spanMin);
      } else if (hasMaxValue) {
        elementsArray.push(spanMax);
      }

      return h('span', {}, [elementsArray]);
    },
  },
};
</script>

<style lang="scss" scoped>
.offer-card__down-item {
  padding: 8px 8px 16px;
}

.offer-card__title-inner {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.text-tooltip {
  font-size: 12px;
  display: inline-block;
  color: #303634;
  padding-top: 6px;
  line-height: 1.4;

  .label {
    color: var(--text-primary);
  }
}

.title-blue {
  background: #f4f7fa;
  border-radius: 4px;
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
}

.offer-card__table {
  &-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e7ecf1;
    padding: 8px;
    font-size: 14px;

    &:last-child {
      border-bottom: none;
    }

    &_right {
      text-align: right;
    }
  }
}

.line {
  position: relative;
  padding-left: 18px;
  padding-bottom: 4px;

  :deep() {
    .offer-card__table {
      margin-left: 14px;
    }
    .offer-card__table-row {
      padding-left: 0!important;

      &_right {
        text-align: right;
      }
    }
  }

  &.root :deep(.offer-card__table) {
    margin-left: 2px;
  }

  .title-blue {
    margin-left: 4px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: -10px;
      top: 50%;
      height: 1px;
      width: 10px;
      background-color: #eaeaea;
    }
  }

  &:not(:last-child) {
    :deep() {
      .offer-card__table {
        &::after {
          content: '';
          position: absolute;
          left: 12px;
          top: 0;
          height: 100%;
          width: 1px;
          background-color: #eaeaea;
        }
      }

      .title-blue::after {
        content: '';
        position: absolute;
        left: -10px;
        top: 0;
        height: 100%;
        width: 1px;
        background-color: #eaeaea;
      }
    }
  }
  &:last-child {
    .title-blue::after {
      content: '';
      position: absolute;
      left: -10px;
      top: 0;
      height: 50%;
      width: 1px;
      background-color: #eaeaea;
    }
  }
}

.rules-container {
  gap: 16px;

  .rule {
    width: 100%;
  }
}

.less-or-equal {
  font-weight: 300!important;
}
</style>
